import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTheme } from "@mui/material";

import { ProductFormProductTransport } from "src/pages/Product/ProductForm/ProductForm.types";
import type { TransportCircuit } from "src/api/client-api/transport/useTransportCircuits";
import { isTransportMutationContractError } from "src/api/client-api/transport/TransportMutationContractError";
import type { TransportReference } from "src/api/sure-api/useSearchTransportReferences";
import { getProductStageTransportLineScoreKey } from "../sectionScores";
import { ProductTransportInlineSelectorView } from "./ProductTransportInlineSelectorView";
import {
    pendingReferenceCircuitMaterializations,
    toFormCircuit,
    type TransportMaterializationFailure,
    type TransportOption,
    type ProductTransportInlineSelectorProps,
    useProductTransportSelectorData,
} from "./useProductTransportSelectorData";

export type { TransportMaterializationPendingChange } from "./useProductTransportSelectorData";

const isExplicitClientRejection = (error: unknown): boolean => {
    const status = (error as { response?: { status?: unknown } })?.response?.status;
    return typeof status === "number" && status >= 400 && status < 500;
};

const getMaterializationFailureKind = (error: unknown): TransportMaterializationFailure["failureKind"] => {
    if (isTransportMutationContractError(error)) {
        return "contract";
    }

    return isExplicitClientRejection(error) ? "request" : "ambiguous";
};

export const ProductTransportInlineSelector = ({
    product,
    setProduct,
    stage,
    anchor,
    draftAnchorKey = null,
    resolvedTransport,
    label,
    disabled = false,
    lineScore = null,
    scoreUnit = "Pts",
    alignScoreColumn = false,
    scoreOnly = false,
    routePickerLayout = false,
    originCountryCode = null,
    onMaterializationPendingChange,
}: ProductTransportInlineSelectorProps) => {
    const selectorId = useId();
    const theme = useTheme();
    const {
        anchorValueId,
        anchorValueIri,
        areTransportCircuitsErrored,
        areTransportCircuitsLoaded,
        areTransportCircuitsLoading,
        areTransportReferencesErrored,
        areTransportReferencesLoaded,
        areTransportReferencesLoading,
        effectiveSelectedOption,
        hasPersistedSelection,
        isManageMyTransportEnabled,
        options,
        orgId,
        orgIri,
        persistedTransportLabel,
        refetchTransportCircuits,
        refetchTransportReferences,
        resolvedOption,
        selectedOption,
        selectedTransport,
        summary,
        transportCircuits,
        upsertTransportCircuit,
    } = useProductTransportSelectorData({
        anchor,
        draftAnchorKey,
        product,
        resolvedTransport,
        stage,
    });
    const materializationContextKey = [
        orgIri ?? "no-organization",
        stage,
        anchor.type,
        anchorValueIri ?? "no-anchor-iri",
        anchorValueId ?? "no-anchor-id",
        draftAnchorKey ?? "no-draft-anchor",
        originCountryCode ?? "no-origin",
        resolvedTransport?.transportReferenceId ?? "no-resolved-reference",
    ].join(":");
    const [failedMaterialization, setFailedMaterialization] = useState<TransportMaterializationFailure | null>(null);
    const activeFailedMaterialization =
        failedMaterialization?.contextKey === materializationContextKey ? failedMaterialization : null;

    const toProductTransport = useCallback(
        (circuit: TransportCircuit): ProductFormProductTransport => ({
            stage,
            productIngredient: anchor.type === "ingredient" ? anchor.value : null,
            productPackaging: anchor.type === "packaging" ? anchor.value : null,
            transportCircuit: toFormCircuit(circuit),
            draftProductIngredientKey: anchor.type === "ingredient" && anchor.value === null ? draftAnchorKey : null,
            ...(anchor.type === "product" ? { lineKey: getProductStageTransportLineScoreKey(stage) } : {}),
        }),
        [anchor, draftAnchorKey, stage],
    );

    const buildProductTransport = async (option: TransportOption): Promise<ProductFormProductTransport | null> => {
        const circuit =
            option.kind === "circuit" ? option.circuit : await materializeReferenceCircuit(option.reference);
        if (circuit === null) {
            return null;
        }

        return toProductTransport(circuit);
    };

    const isSameAnchorTransport = useCallback(
        (transport: ProductFormProductTransport): boolean => {
            if (transport.stage !== stage) {
                return false;
            }

            if (anchor.type === "ingredient") {
                if (anchorValueIri === undefined && draftAnchorKey !== null) {
                    return transport.draftProductIngredientKey === draftAnchorKey;
                }

                return transport.productIngredient?.["@id"] === anchorValueIri;
            }

            if (anchor.type === "packaging") {
                return transport.productPackaging?.["@id"] === anchorValueIri;
            }

            return transport.productIngredient == null && transport.productPackaging == null;
        },
        [anchor.type, anchorValueIri, draftAnchorKey, stage],
    );

    const updateTransports = useCallback(
        (update: (currentTransports: ProductFormProductTransport[]) => ProductFormProductTransport[]) => {
            setProduct((current) => ({
                ...current,
                productTransports: update(current.productTransports ?? []),
            }));
        },
        [setProduct],
    );

    const upsertTransport = useCallback(
        (transport: ProductFormProductTransport) => {
            updateTransports((currentTransports) => {
                const existingIndex = currentTransports.findIndex((item) => isSameAnchorTransport(item));
                if (existingIndex === -1) {
                    return [...currentTransports, transport];
                }

                const existing = currentTransports[existingIndex]!;
                const merged: ProductFormProductTransport = {
                    ...transport,
                    "@id": existing["@id"] ?? transport["@id"],
                    id: existing.id ?? transport.id,
                };
                return currentTransports.map((item, index) => (index === existingIndex ? merged : item));
            });
        },
        [isSameAnchorTransport, updateTransports],
    );

    const removeTransport = useCallback(() => {
        updateTransports((currentTransports) => currentTransports.filter((item) => !isSameAnchorTransport(item)));
    }, [isSameAnchorTransport, updateTransports]);

    const materializeReferenceCircuit = async (reference: TransportReference): Promise<TransportCircuit | null> => {
        if (orgIri == null) {
            return null;
        }

        const existing = transportCircuits.find((circuit) => circuit.sourceReferenceCircuitId === reference.id);
        if (existing) {
            return existing;
        }

        const materializationKey = `${orgIri}:${reference.id}`;
        const pendingMaterialization = pendingReferenceCircuitMaterializations.get(materializationKey);
        if (pendingMaterialization !== undefined) {
            return await pendingMaterialization;
        }

        const materialization = upsertTransportCircuit
            .mutateAsync({
                organization: orgIri,
                linkMode: "LINKED",
                sourceReferenceCircuitId: reference.id,
                name: reference.name ?? reference.code,
                originCountryCode: reference.originCountryCode ?? null,
                destinationCountryCode: reference.destinationCountryCode ?? null,
            })
            .catch((error: unknown) => {
                if (
                    getMaterializationFailureKind(error) === "request" &&
                    pendingReferenceCircuitMaterializations.get(materializationKey) === materialization
                ) {
                    pendingReferenceCircuitMaterializations.delete(materializationKey);
                }

                throw error;
            });
        pendingReferenceCircuitMaterializations.set(materializationKey, materialization);

        return await materialization;
    };

    const syncResolvedTransportRef = useRef<number | null>(null);
    const userClearedTransportRef = useRef(false);
    const userSelectionRequestRef = useRef(0);
    const pendingMaterializationOperationSequenceRef = useRef(0);
    const pendingMaterializationOperationRef = useRef<number | null>(null);
    const onMaterializationPendingChangeRef = useRef(onMaterializationPendingChange);
    onMaterializationPendingChangeRef.current = onMaterializationPendingChange;

    const beginReportedMaterialization = (): number => {
        const operationId = pendingMaterializationOperationSequenceRef.current + 1;
        pendingMaterializationOperationSequenceRef.current = operationId;
        pendingMaterializationOperationRef.current = operationId;
        onMaterializationPendingChangeRef.current?.(selectorId, true);

        return operationId;
    };

    const finishReportedMaterialization = (operationId: number) => {
        if (pendingMaterializationOperationRef.current !== operationId) {
            return;
        }

        pendingMaterializationOperationRef.current = null;
        onMaterializationPendingChangeRef.current?.(selectorId, false);
    };

    const clearReportedMaterializationPending = useCallback(() => {
        if (pendingMaterializationOperationRef.current === null) {
            return;
        }

        pendingMaterializationOperationRef.current = null;
        onMaterializationPendingChangeRef.current?.(selectorId, false);
    }, [selectorId]);

    useEffect(() => {
        userSelectionRequestRef.current += 1;
        userClearedTransportRef.current = false;
        syncResolvedTransportRef.current = null;

        return () => {
            userSelectionRequestRef.current += 1;
            clearReportedMaterializationPending();
        };
    }, [
        anchor.type,
        anchorValueIri,
        anchorValueId,
        draftAnchorKey,
        orgId,
        orgIri,
        resolvedTransport?.lineKey,
        resolvedTransport?.transportReferenceId,
        originCountryCode,
        clearReportedMaterializationPending,
    ]);

    useEffect(() => {
        setFailedMaterialization(null);
    }, [materializationContextKey]);

    const selectedCircuitId = selectedTransport?.transportCircuit?.id ?? 0;
    const needsCircuitMaterialization = selectedTransport === undefined || selectedCircuitId <= 0;
    const resolvedOptionKey = resolvedOption === null ? null : `${resolvedOption.kind}:${resolvedOption.id}`;

    useEffect(() => {
        // Product-stage defaults are scored by live preview from resolvedTransports only.
        // Persisting them into productTransports would send explicit rows on the next preview
        // call and suppress backend defaults before the circuit is fully materialized.
        if (anchor.type === "product" || isManageMyTransportEnabled) {
            return;
        }

        if (disabled || userClearedTransportRef.current || resolvedOption === null || !needsCircuitMaterialization) {
            return;
        }

        const referenceId = resolvedTransport?.transportReferenceId ?? resolvedOption.id;
        if (syncResolvedTransportRef.current === referenceId) {
            return;
        }

        syncResolvedTransportRef.current = referenceId;
        const requestId = userSelectionRequestRef.current;
        let cancelled = false;
        const pendingOperationId = beginReportedMaterialization();

        void (async () => {
            let keepPending = false;
            try {
                const transport = await buildProductTransport(resolvedOption);
                if (
                    !cancelled &&
                    !userClearedTransportRef.current &&
                    requestId === userSelectionRequestRef.current &&
                    transport !== null
                ) {
                    setFailedMaterialization(null);
                    upsertTransport(transport);
                }
            } catch (error) {
                if (!cancelled && requestId === userSelectionRequestRef.current) {
                    keepPending = true;
                    setFailedMaterialization({
                        contextKey: materializationContextKey,
                        failureKind: getMaterializationFailureKind(error),
                        option: resolvedOption,
                    });
                }
            } finally {
                if (!keepPending) {
                    finishReportedMaterialization(pendingOperationId);
                }
            }
        })();

        return () => {
            cancelled = true;
            finishReportedMaterialization(pendingOperationId);
            if (syncResolvedTransportRef.current === referenceId) {
                syncResolvedTransportRef.current = null;
            }
        };
        // upsertTransport resolves against the latest product state through functional setProduct.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        anchor.type,
        anchorValueIri,
        anchorValueId,
        disabled,
        draftAnchorKey,
        isManageMyTransportEnabled,
        materializationContextKey,
        orgIri,
        resolvedOptionKey,
        resolvedTransport?.transportReferenceId,
        needsCircuitMaterialization,
    ]);

    const selectTransportOption = async (option: TransportOption) => {
        const requestId = userSelectionRequestRef.current + 1;
        userSelectionRequestRef.current = requestId;
        userClearedTransportRef.current = false;
        setFailedMaterialization(null);

        const reportsPendingMaterialization = option.kind === "reference";
        if (!reportsPendingMaterialization) {
            clearReportedMaterializationPending();
        }
        const pendingOperationId = reportsPendingMaterialization ? beginReportedMaterialization() : null;
        let keepPending = false;

        try {
            const transport = await buildProductTransport(option);
            if (requestId === userSelectionRequestRef.current && transport !== null) {
                upsertTransport(transport);
            }
        } catch (error) {
            if (reportsPendingMaterialization && requestId === userSelectionRequestRef.current) {
                keepPending = true;
                setFailedMaterialization({
                    contextKey: materializationContextKey,
                    failureKind: getMaterializationFailureKind(error),
                    option,
                });
            }
        } finally {
            if (pendingOperationId !== null && !keepPending) {
                finishReportedMaterialization(pendingOperationId);
            }
        }
    };

    const routeFieldMinHeight = routePickerLayout ? theme.spacing(5) : theme.spacing(5.5);

    const reconcileAmbiguousMaterialization = async (failure: TransportMaterializationFailure) => {
        const { option } = failure;
        if (option.kind !== "reference" || orgIri === undefined) {
            return;
        }

        const requestId = userSelectionRequestRef.current;
        setFailedMaterialization(null);

        try {
            const refreshed = await refetchTransportCircuits();
            if (requestId !== userSelectionRequestRef.current) {
                return;
            }

            if (refreshed.isError || refreshed.data === undefined) {
                throw refreshed.error ?? new Error("Transport circuit refresh returned no data");
            }

            const persistedCircuit = refreshed.data.find(
                (circuit) => circuit.sourceReferenceCircuitId === option.reference.id,
            );
            if (persistedCircuit !== undefined) {
                upsertTransport(toProductTransport(persistedCircuit));
                clearReportedMaterializationPending();
                return;
            }

            setFailedMaterialization({
                contextKey: materializationContextKey,
                failureKind: failure.failureKind === "contract" ? "contract" : "ambiguous",
                option,
            });
        } catch {
            if (requestId !== userSelectionRequestRef.current) {
                return;
            }

            setFailedMaterialization({
                contextKey: materializationContextKey,
                failureKind: "refresh",
                option,
            });
        }
    };

    const retryFailedMaterialization = (failure: NonNullable<typeof activeFailedMaterialization>) => {
        if (failure.failureKind !== "request") {
            return reconcileAmbiguousMaterialization(failure);
        }

        return selectTransportOption(failure.option);
    };

    const isActiveCatalogueLoading =
        isManageMyTransportEnabled ? areTransportCircuitsLoading : (
            areTransportReferencesLoading || areTransportCircuitsLoading
        );
    const isActiveCatalogueErrored =
        isManageMyTransportEnabled ? areTransportCircuitsErrored : (
            areTransportReferencesErrored || areTransportCircuitsErrored
        );
    const isActiveCatalogueLoaded =
        isManageMyTransportEnabled ? areTransportCircuitsLoaded : (
            areTransportReferencesLoaded && areTransportCircuitsLoaded
        );
    const retryActiveCatalogue = () => {
        if (isManageMyTransportEnabled || areTransportCircuitsErrored) {
            void refetchTransportCircuits();
        }

        if (!isManageMyTransportEnabled && areTransportReferencesErrored) {
            void refetchTransportReferences();
        }
    };
    return (
        <ProductTransportInlineSelectorView
            alignScoreColumn={alignScoreColumn}
            anchorIsSelectable={anchor.type === "product" || anchor.value !== null || draftAnchorKey !== null}
            catalogueError={isActiveCatalogueErrored}
            catalogueLoaded={isActiveCatalogueLoaded}
            catalogueLoading={isActiveCatalogueLoading}
            disabled={disabled}
            effectiveSelectedOption={effectiveSelectedOption}
            hasPersistedSelection={hasPersistedSelection}
            isManageMyTransportEnabled={isManageMyTransportEnabled}
            label={label}
            lineScore={lineScore}
            materializationError={activeFailedMaterialization}
            onOptionChange={(option) => {
                if (option === null) {
                    userSelectionRequestRef.current += 1;
                    userClearedTransportRef.current = true;
                    setFailedMaterialization(null);
                    clearReportedMaterializationPending();
                    removeTransport();
                    return;
                }

                void selectTransportOption(option);
            }}
            onRetryCatalogue={retryActiveCatalogue}
            onRetryMaterialization={retryFailedMaterialization}
            options={options}
            persistedTransportLabel={persistedTransportLabel}
            resolvedTransportLabel={resolvedTransport?.label}
            routeFieldMinHeight={routeFieldMinHeight}
            routePickerLayout={routePickerLayout}
            scoreOnly={scoreOnly}
            scoreUnit={scoreUnit}
            selectedOption={selectedOption}
            summary={summary}
        />
    );
};
